import { FlexColumn, Loading } from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { AlertDestinationForm } from 'shared/components/AlertDestinations';
import { AlertDestinationFormData } from 'shared/types';
import useCustomAlertOverride from 'shared/hooks/useCustomAlertOverride';
import { fontWeight } from '@elementary/common-ui/style/utils';
import { AlertDestination } from '../../../types';
import AddAlertDestinationButton from './AddAlertDestinationButton';

type Props = {
  editItem?: AlertDestination;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  showAddDestinationButton?: boolean;
  currentOverrides?: AlertDestination[];
  latestExecutionTestId?: string;
};

const AlertOverrideSection = ({
  editItem,
  isOpen,
  onOpen,
  onClose,
  showAddDestinationButton,
  currentOverrides,
  latestExecutionTestId,
}: Props) => {
  const {
    alertDestinationOptions,
    alertProviders,
    alertDestinationsError,
    alertDestinationsLoading,
    getDestinationItem,
    customAlertOverride: {
      isCreateCustomAlertOverrideLoading,
      createCustomAlertOverrideMutation,
      createCustomAlertOverrideError,
    },
  } = useCustomAlertOverride();

  const onSubmit = async (data: AlertDestinationFormData) => {
    let alertArg;

    if (data.alertProvider === 'email') {
      const emailDestinations = Array.isArray(data.destination)
        ? data.destination
        : [];
      alertArg = [
        {
          channelName: JSON.stringify({
            to: emailDestinations.map((dest) => dest.value || ''),
          }),
          destination: 'email' as const,
        },
      ];
    } else {
      const destination = !Array.isArray(data.destination)
        ? data.destination
        : data.destination[0];

      alertArg = [
        {
          channelName: destination?.value || '',
          destination: data.alertProvider,
        },
      ];
    }

    const formatExistingOverrides = (overrides: typeof currentOverrides) =>
      overrides?.map((dest) => ({
        channelName: dest.channel,
        destination: dest.type,
      }));

    if (editItem) {
      await createCustomAlertOverrideMutation({
        alertOverride: alertArg,
        existingOverrides: formatExistingOverrides(
          currentOverrides?.filter((dest) => dest.id !== editItem.id),
        ),
        entityIds: latestExecutionTestId ? [latestExecutionTestId] : [],
      });
    } else {
      await createCustomAlertOverrideMutation({
        alertOverride: alertArg,
        existingOverrides: formatExistingOverrides(currentOverrides),
        entityIds: latestExecutionTestId ? [latestExecutionTestId] : [],
      });
    }
    onClose();
  };

  if (isOpen || editItem) {
    if (alertDestinationsLoading) {
      return <Loading size={32} />;
    }

    return (
      <FlexColumn sx={{ gap: 1.5 }}>
        <Typography sx={{ fontWeight: fontWeight('semibold') }}>
          Set Custom Alert Destination
        </Typography>
        <AlertDestinationForm
          onClose={() => {
            onClose();
          }}
          alertDestination={
            editItem
              ? {
                  ...editItem,
                  channel: getDestinationItem(editItem.type, editItem.channel),
                }
              : undefined
          }
          destinationTypes={alertProviders}
          destinationOptions={alertDestinationOptions}
          onSubmit={onSubmit}
          isLoading={isCreateCustomAlertOverrideLoading}
          error={!!createCustomAlertOverrideError}
          providersError={alertDestinationsError}
        />
      </FlexColumn>
    );
  }

  return showAddDestinationButton ? (
    <AddAlertDestinationButton
      onClick={onOpen}
      mode={currentOverrides?.length ? 'add' : 'set'}
    />
  ) : null;
};

export default AlertOverrideSection;
